import type { CallScriptSaveWarning } from "../api/callScriptShared.types";
import { groupWarningsByCode } from "./callScriptComparison";

// ---------------------------------------------------------------------------
// CallScriptSaveBanners
//
// The save feedback strip both Call Scripts render above the comparison grid:
// a hard error when the save failed, and/or the non-fatal warnings a
// successful save can still carry (e.g. a field the backend couldn't write
// back). Warnings are grouped by `code` so several sharing one code render
// under a single heading instead of repeating it.
// ---------------------------------------------------------------------------

interface CallScriptSaveBannersProperties {
  /** Set when the save itself failed — already formatted for display. */
  readonly error?: string;
  /** Non-fatal issues from a save that otherwise succeeded. */
  readonly warnings?: CallScriptSaveWarning[];
  /** Resolves a warning group's heading from its `code`. */
  readonly warningHeading?: (code: string) => string;
}

export function CallScriptSaveBanners({ error, warnings = [], warningHeading }: CallScriptSaveBannersProperties) {
  return (
    <>
      {error && (
        <p
          role="alert"
          className="text-destructive mt-(--spacing-2) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) text-center"
        >
          {error}
        </p>
      )}
      {warnings.length > 0 && (
        <div role="alert" className="mt-(--spacing-2) gap-(--spacing-2) flex flex-col items-center">
          {groupWarningsByCode(warnings).map((group) => (
            <div key={group.code} className="text-center">
              <p className="text-destructive text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
                {warningHeading?.(group.code)}
              </p>
              <ul className="text-destructive text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) list-inside list-disc">
                {group.details.map((detail, detailIndex) => (
                  // Indexed, not keyed by `detail` text — two warnings in the
                  // same group can share identical detail text.
                  // eslint-disable-next-line react/no-array-index-key -- static list rebuilt wholesale per save; no reordering/insertion to key by
                  <li key={detailIndex}>{detail}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
